import { useCallback, useState, type ReactNode } from "react"
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  TradesTable,
  type TradeSubTab,
  type TradesTableProps,
} from "../TradesTable/TradesTable"

export interface TradeHistoryPanelProps extends TradesTableProps {
  isEmpty?: boolean
  emptyLabel?: ReactNode
  showSubTabsWhenEmpty?: boolean
  emptySubTabs?: TradeSubTab[]
  defaultEmptySubTabId?: string
  onEmptySubTabChange?: (id: string) => void
  className?: string
}

const DEFAULT_EMPTY_SUBTABS: TradeSubTab[] = [
  { id: "history", label: "Trades" },
  { id: "open", label: "Open Trades" },
  { id: "pending", label: "Pending Orders" },
]

export function TradeHistoryPanel({
  isEmpty = false,
  emptyLabel,
  showSubTabsWhenEmpty = true,
  emptySubTabs,
  defaultEmptySubTabId = "history",
  onEmptySubTabChange,
  className,
  ...tableProps
}: TradeHistoryPanelProps) {
  const resolvedSubTabs =
    emptySubTabs ?? tableProps.subTabs ?? DEFAULT_EMPTY_SUBTABS

  const [internalSubTab, setInternalSubTab] =
    useState<string>(defaultEmptySubTabId)

  const handleSubTab = useCallback(
    (id: string) => {
      setInternalSubTab(id)
      onEmptySubTabChange?.(id)
    },
    [onEmptySubTabChange],
  )

  if (isEmpty) {
    const prompt = emptyLabel ?? (
      <>
        Your trade history will populate here after your first backtest
        <ArrowRight
          size={14}
          strokeWidth={1.5}
          className="hidden min-[1100px]:inline-block align-middle ml-1"
        />
      </>
    )

    if (!showSubTabsWhenEmpty || tableProps.hideSubTabs) {
      return (
        <div
          className={cn(
            "h-full flex items-center justify-center px-6",
            className,
          )}
        >
          <p className="text-caption text-muted-foreground text-center leading-relaxed">
            {prompt}
          </p>
        </div>
      )
    }

    return (
      <div className={cn("flex flex-col h-full min-h-0", className)}>
        <div className="flex items-center gap-4 px-4 border-b border-border shrink-0 h-11">
          {resolvedSubTabs.map((tab) => {
            const active = tab.id === internalSubTab
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleSubTab(tab.id)}
                className={cn(
                  "text-caption pb-1 cursor-pointer transition-colors border-b-[1.5px]",
                  active
                    ? "font-medium text-foreground border-foreground"
                    : "font-normal text-muted-foreground border-transparent hover:text-foreground",
                )}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        <div className="flex-1 min-h-0 flex items-center justify-center px-6">
          <p className="text-caption text-muted-foreground text-center leading-relaxed">
            {prompt}
          </p>
        </div>
      </div>
    )
  }

  return <TradesTable className={className} {...tableProps} />
}
